// Error Recovery System for VRP Unified Architecture
import { VRPError, VRPErrorCategory, VRPErrorSeverity } from '../types/errors';

export class VRPErrorRecovery {
  private static readonly MAX_RETRY_ATTEMPTS = 3;
  private static readonly RETRY_DELAYS = [1000, 2000, 5000]; // Progressive delays

  static async attemptRecovery(error: VRPError): Promise<boolean> {
    console.log(`[ErrorRecovery] Attempting recovery for ${error.category}:`, error.message);

    try {
      switch (error.category) {
        case VRPErrorCategory.CONTEXT_ERROR:
          return await this.recoverFromContextError(error);
        
        case VRPErrorCategory.STATE_MANAGEMENT_ERROR:
          return await this.recoverFromStateError(error);
        
        case VRPErrorCategory.NAVIGATION_ERROR:
          return await this.recoverFromNavigationError(error);
        
        case VRPErrorCategory.NETWORK_ERROR:
          return await this.recoverFromNetworkError(error);
        
        case VRPErrorCategory.COMPONENT_ERROR:
          return await this.recoverFromComponentError(error);
        
        default:
          return await this.attemptGenericRecovery(error);
      }
    } catch (recoveryError) {
      console.error('[ErrorRecovery] Recovery attempt failed:', recoveryError);
      return false;
    }
  }

  private static async recoverFromContextError(error: VRPError): Promise<boolean> {
    console.log('[ErrorRecovery] Recovering from context error...');
    
    // For React Error #185, force page reload to reset context state
    if (error.message.includes('useProject') || error.message.includes('Context')) {
      this.forceApplicationReload();
      return true;
    }
    
    // Try to reinitialize contexts
    try {
      // Clear any corrupted state in localStorage
      this.clearCorruptedState();
      
      // Force a navigation reset
      window.history.pushState({}, '', '/dashboard');
      window.location.reload();
      
      return true;
    } catch {
      return false;
    }
  }

  private static async recoverFromStateError(error: VRPError): Promise<boolean> {
    console.log('[ErrorRecovery] Recovering from state management error...');
    
    // For infinite loops, force reload
    if (error.message.includes('Maximum update depth') || 
        error.message.includes('Too many re-renders')) {
      this.forceApplicationReload();
      return true;
    }
    
    return false;
  }

  private static async recoverFromNavigationError(error: VRPError): Promise<boolean> {
    console.log('[ErrorRecovery] Recovering from navigation error...');
    
    try {
      // Navigate to safe route (dashboard)
      window.history.pushState({}, '', '/dashboard');
      
      // Clear any navigation-related state
      sessionStorage.removeItem('vrp_navigation_state');
      
      return true;
    } catch {
      return false;
    }
  }

  private static async recoverFromNetworkError(error: VRPError): Promise<boolean> {
    console.log('[ErrorRecovery] Recovering from network error...');
    
    const retryCount = error.retryCount || 0;
    
    if (retryCount >= this.MAX_RETRY_ATTEMPTS) {
      console.log('[ErrorRecovery] Max retry attempts reached for network error');
      return false;
    }
    
    // Wait before retry
    const delay = this.RETRY_DELAYS[retryCount] || this.RETRY_DELAYS[this.RETRY_DELAYS.length - 1];
    await this.delay(delay);
    
    // Check network connectivity
    if (!navigator.onLine) {
      console.log('[ErrorRecovery] No network connection, waiting...');
      return false;
    }
    
    // Increment retry count
    error.retryCount = retryCount + 1;
    
    return true;
  }

  private static async recoverFromComponentError(error: VRPError): Promise<boolean> {
    console.log('[ErrorRecovery] Recovering from component error...');
    
    try {
      // Clear component-specific state that might be corrupted
      this.clearComponentState(error.context.component);
      
      // Force re-render by updating a timestamp in localStorage
      localStorage.setItem('vrp_force_rerender', Date.now().toString());
      
      return true;
    } catch {
      return false;
    }
  }

  private static async attemptGenericRecovery(error: VRPError): Promise<boolean> {
    console.log('[ErrorRecovery] Attempting generic recovery...');
    
    // For unknown errors, try basic recovery steps
    try {
      // Clear potentially corrupted state
      this.clearCorruptedState();
      
      // If error is critical, force reload
      if (error.severity === VRPErrorSeverity.CRITICAL) {
        this.forceApplicationReload();
        return true;
      }
      
      return false;
    } catch {
      return false;
    }
  }

  private static forceApplicationReload(): void {
    console.log('[ErrorRecovery] Forcing application reload...');
    
    // Set flag to indicate forced reload
    sessionStorage.setItem('vrp_forced_reload', 'true');
    sessionStorage.setItem('vrp_reload_reason', 'error_recovery');
    
    // Clear potentially corrupted state
    this.clearCorruptedState();
    
    // Force reload
    window.location.reload();
  }

  private static clearCorruptedState(): void {
    console.log('[ErrorRecovery] Clearing potentially corrupted state...');
    
    // Clear specific VRP state items that might be corrupted
    const keysToRemove = [
      'vrp_ui_state',
      'vrp_navigation_state',
      'vrp_data_cache',
      'vrp_user_preferences'
    ];
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
  }

  private static clearComponentState(component?: string): void {
    if (!component) return;
    
    console.log(`[ErrorRecovery] Clearing state for component: ${component}`);
    
    // Clear component-specific state
    localStorage.removeItem(`vrp_${component}_state`);
    sessionStorage.removeItem(`vrp_${component}_state`);
  }

  private static delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  static markAsRecovered(error: VRPError, recoveryMethod: string): VRPError {
    return {
      ...error,
      recovered: true,
      recoveryMethod,
      metadata: {
        ...error.metadata,
        recoveredAt: Date.now()
      }
    };
  }

  static shouldAttemptRecovery(error: VRPError): boolean {
    // Don't attempt recovery if already recovered
    if (error.recovered) {
      return false;
    }
    
    // Don't attempt recovery if max retries reached
    if ((error.retryCount || 0) >= this.MAX_RETRY_ATTEMPTS) {
      return false;
    }
    
    // Always attempt recovery for critical errors
    if (error.severity === VRPErrorSeverity.CRITICAL) {
      return true;
    }
    
    // Attempt recovery for high severity errors
    if (error.severity === VRPErrorSeverity.HIGH) {
      return true;
    }
    
    // Attempt recovery for medium severity errors on first occurrence
    if (error.severity === VRPErrorSeverity.MEDIUM && (error.retryCount || 0) === 0) {
      return true;
    }
    
    return false;
  }
}